/**
 * DOM side of `useGuideHighlight()`: expand the host accordion(s), scroll the
 * anchor into view, pulse its outline twice (WAAPI) — or a static 1.2 s outline
 * under prefers-reduced-motion — and stop on the first interaction (AC-010).
 */
import {
  ACCORDION_SETTLE_MS,
  LOOKUP_ATTEMPTS,
  LOOKUP_INTERVAL_MS,
  PULSE_DURATION_MS,
  PULSE_ITERATIONS,
  REDUCED_MOTION_OUTLINE_MS,
} from './guideHighlight';

export const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'focusin'] as const;

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Click the summary of every collapsed MUI Accordion ancestor (controlled accordions update their own state + localStorage). */
export function expandCollapsedAccordions(target: HTMLElement): number {
  let expanded = 0;
  let node: HTMLElement | null = target.parentElement;
  while (node) {
    if (node.classList.contains('MuiAccordion-root') && !node.classList.contains('Mui-expanded')) {
      // MUI v7 wraps AccordionSummary in an intermediate `.MuiAccordion-heading` div
      // (older MUI kept it a direct child) — match both shapes without reaching into
      // a nested Accordion's own summary several levels down.
      const summary = node.querySelector<HTMLElement>(
        ':scope > .MuiAccordionSummary-root, :scope > .MuiAccordion-heading > .MuiAccordionSummary-root',
      );
      if (summary) {
        summary.click();
        expanded += 1;
      }
    }
    node = node.parentElement;
  }
  return expanded;
}

export function pulseGuideTarget(target: HTMLElement, color: string, reducedMotion: boolean): () => void {
  const previousOutline = target.style.outline;
  const previousOffset = target.style.outlineOffset;
  let animation: Animation | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let done = false;

  const cleanup = () => {
    if (done) return;
    done = true;
    animation?.cancel();
    if (timer) clearTimeout(timer);
    target.style.outline = previousOutline;
    target.style.outlineOffset = previousOffset;
    INTERACTION_EVENTS.forEach((eventName) => target.removeEventListener(eventName, cleanup));
  };
  INTERACTION_EVENTS.forEach((eventName) => target.addEventListener(eventName, cleanup));

  target.style.outlineOffset = '4px';
  if (reducedMotion || typeof target.animate !== 'function') {
    target.style.outline = `3px solid ${color}`;
    timer = setTimeout(cleanup, REDUCED_MOTION_OUTLINE_MS);
  } else {
    animation = target.animate(
      [
        { outline: '3px solid transparent' },
        { outline: `3px solid ${color}` },
        { outline: '3px solid transparent' },
      ],
      { duration: PULSE_DURATION_MS, iterations: PULSE_ITERATIONS, easing: 'ease-in-out' },
    );
    animation.onfinish = cleanup;
  }
  return cleanup;
}

export interface HighlightJob {
  cancel: () => void;
}

export function startHighlightJob(testId: string, color: string, onDone: () => void): HighlightJob {
  let attempts = 0;
  let cleanupPulse: (() => void) | null = null;
  let settleTimer: ReturnType<typeof setTimeout> | null = null;

  const finish = () => {
    clearInterval(poll);
    onDone();
  };

  const highlight = (element: HTMLElement) => {
    const reduced = prefersReducedMotion();
    if (typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
    }
    cleanupPulse = pulseGuideTarget(element, color, reduced);
    finish();
  };

  const poll = setInterval(() => {
    attempts += 1;
    const element = document.querySelector<HTMLElement>(`[data-testid="${testId}"]`);
    if (!element) {
      if (attempts >= LOOKUP_ATTEMPTS) finish();
      return;
    }
    clearInterval(poll);
    const expanded = expandCollapsedAccordions(element);
    if (expanded > 0) {
      settleTimer = setTimeout(() => highlight(element), ACCORDION_SETTLE_MS);
    } else {
      highlight(element);
    }
  }, LOOKUP_INTERVAL_MS);

  return {
    cancel: () => {
      clearInterval(poll);
      if (settleTimer) clearTimeout(settleTimer);
      cleanupPulse?.();
    },
  };
}
